import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Loader2, Users, UserPlus, Trash2, CheckCircle2, Clock, Search,
} from "lucide-react";

export const ListParticipantsDialog = ({ open, onOpenChange, list, onChanged }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const authHeaders = {
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  };

  const load = useCallback(async () => {
    if (!list?.id) return;
    setLoading(true);
    try {
      const [sigsR, empsR] = await Promise.all([
        axios.get(`${API}/admin/courses/lists/${list.id}/signatures`, authHeaders),
        axios.get(`${API}/admin/employees-for-list`, { timeout: 30000 }),
      ]);
      setItems(sigsR.data?.items || []);
      setAllEmployees(Array.isArray(empsR.data) ? empsR.data : (empsR.data?.employees || []));
    } catch (e) {
      toast.error("Erro ao carregar participantes");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list?.id]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelectedIds([]);
      load();
    }
  }, [open, load]);

  const inListIds = useMemo(
    () => new Set(items.map((i) => i.employee_id)), [items]);

  const candidates = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (q.length < 2) return [];
    return allEmployees
      .filter((e) => !inListIds.has(e.id))
      .filter((e) =>
        (e.nome || "").toUpperCase().includes(q) ||
        (e.matricula || "").includes(q))
      .slice(0, 8);
  }, [search, allEmployees, inListIds]);

  const toggleSelect = (emp) => {
    setSelectedIds((prev) =>
      prev.includes(emp.id)
        ? prev.filter((i) => i !== emp.id)
        : [...prev, emp.id]
    );
  };

  const addSelected = async () => {
    setAdding(true);
    try {
      const r = await axios.post(
        `${API}/admin/courses/lists/${list.id}/participants`,
        { employee_ids: selectedIds }, authHeaders);
      toast.success(
        `${r.data.added} pessoa(s) adicionada(s) — convocações sendo enviadas`);
      if (r.data.sem_contato?.length) {
        toast.warning(
          `${r.data.sem_contato.length} sem telefone/e-mail: verão a convocação só no portal`);
      }
      setSelectedIds([]);
      setSearch("");
      load();
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao adicionar");
    } finally {
      setAdding(false);
    }
  };

  const removeOne = async (sig) => {
    const signedWarn = sig.status === "signed"
      ? "\n\nATENÇÃO: esta pessoa JÁ ASSINOU — a assinatura dela será apagada da lista."
      : "";
    if (!window.confirm(`Remover ${sig.nome} da lista?${signedWarn}`)) return;
    setRemovingId(sig.employee_id);
    try {
      await axios.delete(
        `${API}/admin/courses/lists/${list.id}/participants/${sig.employee_id}`,
        authHeaders);
      toast.success(`${sig.nome} removido(a) da lista`);
      setItems((prev) => prev.filter((i) => i.employee_id !== sig.employee_id));
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao remover");
    } finally {
      setRemovingId(null);
    }
  };

  const signedCount = items.filter((i) => i.status === "signed").length;
  const isClosed = list?.status === "closed";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="participants-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-700" />
            Participantes da lista
          </DialogTitle>
          <DialogDescription>
            {list?.training_date ? `Lista de ${list.training_date} — ` : ""}
            {items.length} participante(s), {signedCount} assinado(s).
            {isClosed
              ? " Lista encerrada: edição indisponível."
              : " Adicione ou remova pessoas; quem entrar recebe a convocação na hora."}
          </DialogDescription>
        </DialogHeader>

        {!isClosed && (
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Buscar por nome ou matrícula para adicionar…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 bg-white"
                data-testid="participants-add-search"
              />
            </div>
            {candidates.length > 0 && (
              <div className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
                {candidates.map((emp) => (
                  <button
                    key={emp.id}
                    type="button"
                    onClick={() => toggleSelect(emp)}
                    data-testid={`participants-candidate-${emp.id}`}
                    className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between hover:bg-slate-50 ${
                      selectedIds.includes(emp.id) ? "bg-emerald-50" : ""}`}
                  >
                    <span className="truncate">
                      {emp.nome}
                      <span className="text-slate-400"> ({emp.matricula})</span>
                    </span>
                    {selectedIds.includes(emp.id) && (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
            {selectedIds.length > 0 && (
              <Button
                size="sm"
                onClick={addSelected}
                disabled={adding}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="participants-add-btn"
              >
                {adding
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <UserPlus className="w-4 h-4 mr-1.5" />}
                Adicionar {selectedIds.length} à lista e convocar
              </Button>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((sig) => (
              <div key={sig.id} className="py-2 flex items-center gap-3" data-testid={`participant-row-${sig.employee_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {sig.nome}
                    <span className="text-slate-400 font-normal"> ({sig.matricula})</span>
                  </div>
                </div>
                {sig.status === "signed" ? (
                  <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Assinado
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                    <Clock className="w-3 h-3 mr-1" /> Pendente
                  </Badge>
                )}
                {!isClosed && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeOne(sig)}
                    disabled={removingId === sig.employee_id}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                    data-testid={`participant-remove-${sig.employee_id}`}
                    title="Remover da lista"
                  >
                    {removingId === sig.employee_id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
